import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Pagination,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { adminService, AdminPaymentOrder } from '../services/adminService';
import { paymentSettingsService } from '../services/paymentSettingsService';

const statusColor = (status: AdminPaymentOrder['status']) =>
  status === 'paid' ? 'success' : status === 'refunded' ? 'info' : status === 'failed' ? 'error' : 'warning';

const BillingPayments: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState('');
  const [orderType, setOrderType] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['adminPaymentOrders', { page, search, status, orderType }],
    queryFn: () => adminService.getPaymentOrders({
      page,
      limit: 20,
      ...(search ? { search } : {}),
      ...(status ? { status } : {}),
      ...(orderType ? { orderType } : {}),
    }),
    keepPreviousData: true,
  });

  const { data: paymentSettings } = useQuery({
    queryKey: ['paymentSettingsForRefund'],
    queryFn: () => paymentSettingsService.getSettings(),
  });
  const creditValueInr = Number((paymentSettings as any)?.creditValueInr) || 10;

  // ── Refund dialog state ───────────────────────────────────────────────────
  const [refundOrder, setRefundOrder] = useState<AdminPaymentOrder | null>(null);
  const [refundMode, setRefundMode] = useState<'gateway' | 'wallet'>('wallet');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [alsoCancelSub, setAlsoCancelSub] = useState(false);

  const openRefund = (order: AdminPaymentOrder) => {
    setRefundOrder(order);
    setRefundMode(order.gateway === 'cashfree' ? 'gateway' : 'wallet');
    setRefundAmount(String(order.amount));
    setRefundReason('');
    setAlsoCancelSub(false);
  };

  const refundMutation = useMutation({
    mutationFn: () => adminService.refundPaymentOrder(refundOrder!.id, {
      mode: refundMode,
      amount: Number(refundAmount),
      reason: refundReason,
      cancelSubscription: alsoCancelSub,
    }),
    onSuccess: (r) => {
      setRefundOrder(null);
      queryClient.invalidateQueries({ queryKey: ['adminPaymentOrders'] });
      toast.success(r.message || 'Refund processed');
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Refund failed'),
  });

  const checkStatusMutation = useMutation({
    mutationFn: (orderId: string) => adminService.getRefundStatus(orderId),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['adminPaymentOrders'] });
      toast.success(`Gateway refund status: ${r.order.refund?.status || r.gatewayStatus || 'unknown'}`);
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Status check failed'),
  });

  const amountNumber = Number(refundAmount);
  const amountError = refundOrder
    ? (!Number.isFinite(amountNumber) || amountNumber <= 0 || amountNumber > refundOrder.amount
      ? `Must be between 0 and ₹${refundOrder.amount}`
      : '')
    : '';
  const walletCredits = Number.isFinite(amountNumber) ? Math.floor(amountNumber / creditValueInr) : 0;

  const orders = data?.orders || [];
  const pagination = data?.pagination;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Billing &amp; Refunds</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <TextField
            size="small"
            placeholder="Search driver name or email…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(searchInput.trim()); setPage(1); } }}
            sx={{ minWidth: 260 }}
          />
          <Select size="small" displayEmpty value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ minWidth: 140 }}>
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="refunded">Refunded</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="created">Created</MenuItem>
          </Select>
          <Select size="small" displayEmpty value={orderType} onChange={(e) => { setOrderType(e.target.value); setPage(1); }} sx={{ minWidth: 140 }}>
            <MenuItem value="">All types</MenuItem>
            <MenuItem value="plan">Plan</MenuItem>
            <MenuItem value="top_up">Top-up</MenuItem>
          </Select>
          <Button variant="outlined" size="small" onClick={() => { setSearch(searchInput.trim()); setPage(1); }}>Apply</Button>
        </Stack>
      </Paper>

      <Paper>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>Driver</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell>Gateway</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.length === 0 && (
                  <TableRow><TableCell colSpan={7}><Typography color="text.secondary" py={2} textAlign="center">No payment orders found.</Typography></TableCell></TableRow>
                )}
                {orders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{order.driver?.name || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{order.driver?.email || ''}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{order.itemName}</Typography>
                      {order.couponCode && (
                        <Typography variant="caption" color="text.secondary">coupon {order.couponCode} (−₹{order.discountAmount})</Typography>
                      )}
                    </TableCell>
                    <TableCell>{order.gateway}</TableCell>
                    <TableCell align="right">₹{order.amount}</TableCell>
                    <TableCell>
                      <Tooltip title={order.refund ? `${order.refund.mode} refund · ₹${order.refund.amount}${order.refund.credits ? ` · ${order.refund.credits} credits` : ''} · ${order.refund.status}` : ''}>
                        <Chip size="small" label={order.status} color={statusColor(order.status)} variant="outlined" />
                      </Tooltip>
                      {order.refund?.status === 'pending' && (
                        <Chip size="small" label="refund pending" color="warning" sx={{ ml: 0.5 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{new Date(order.createdAt).toLocaleDateString('en-IN')}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      {order.status === 'paid' && (
                        <Button size="small" variant="outlined" startIcon={<CurrencyRupeeIcon />} onClick={() => openRefund(order)}>
                          Refund
                        </Button>
                      )}
                      {order.refund?.status === 'pending' && (
                        <Button
                          size="small"
                          startIcon={<ReplayIcon />}
                          disabled={checkStatusMutation.isLoading}
                          onClick={() => checkStatusMutation.mutate(order.id)}
                        >
                          Check status
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {pagination && pagination.pages > 1 && (
          <Box display="flex" justifyContent="center" py={1.5}>
            <Pagination count={pagination.pages} page={page} onChange={(_e, value) => setPage(value)} size="small" />
          </Box>
        )}
      </Paper>

      {/* Refund dialog */}
      <Dialog open={Boolean(refundOrder)} onClose={() => setRefundOrder(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Refund {refundOrder?.itemName}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={0.5}>
            <Typography variant="body2" color="text.secondary">
              {refundOrder?.driver?.name} · paid ₹{refundOrder?.amount} via {refundOrder?.gateway}
            </Typography>
            <RadioGroup value={refundMode} onChange={(e) => setRefundMode(e.target.value as 'gateway' | 'wallet')}>
              <Tooltip title={refundOrder?.gateway !== 'cashfree' ? 'Gateway refunds are only available for Cashfree orders' : ''}>
                <span>
                  <FormControlLabel
                    value="gateway"
                    control={<Radio size="small" />}
                    disabled={refundOrder?.gateway !== 'cashfree'}
                    label="Gateway refund — money back to the original payment method (5-7 days)"
                  />
                </span>
              </Tooltip>
              <FormControlLabel
                value="wallet"
                control={<Radio size="small" />}
                label={`Wallet credits — instant (${walletCredits} credits at ₹${creditValueInr}/credit)`}
              />
            </RadioGroup>
            <TextField
              size="small"
              label="Amount (₹)"
              type="number"
              value={refundAmount}
              onChange={(e) => setRefundAmount(e.target.value)}
              error={Boolean(amountError)}
              helperText={amountError || `Full amount: ₹${refundOrder?.amount}`}
            />
            <TextField
              size="small"
              label="Reason"
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              multiline
              minRows={2}
            />
            {refundOrder?.orderType === 'plan' && (
              <FormControlLabel
                control={<Checkbox size="small" checked={alsoCancelSub} onChange={(e) => setAlsoCancelSub(e.target.checked)} />}
                label="Also cancel the driver's subscription immediately"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundOrder(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={Boolean(amountError) || (refundMode === 'wallet' && walletCredits < 1) || refundMutation.isLoading}
            onClick={() => refundMutation.mutate()}
          >
            {refundMutation.isLoading ? 'Refunding…' : `Refund ₹${refundAmount || 0}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingPayments;
